import React, { useState, useEffect, useCallback } from "react";
import { FaCalendarCheck, FaSpinner, FaCheck, FaFileInvoiceDollar, FaUsers, FaMoneyBillWave, FaExclamationTriangle } from "react-icons/fa";
import ScreenHeader from "../../components/ScreenHeader";
import LoadingState from "../../components/LoadingState";
import { useBuilding } from "../../context/BuildingContext";
import { db, auth, storage } from "../../firebase";
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { buildLiquidacionPdf } from "../../utils/liquidacionPdf";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import { fetchUsersByUserIds, tolerantId } from "../../utils/firestoreHelpers";
import { consultaDelCierre, leerGastosPorId } from "../../utils/consultasEdificio";
import { approveMontlyaccountCierre } from "../../utils/cierreApprove";
import { formatCurrencyUY } from "../../utils/formatters";
import { friendlyBreakdownName } from "../../utils/fundHelpers";
import { esUnidadDelEdificio } from "../../utils/unidadesEdificio";
import { detalleCorto } from "../../utils/detalleGasto";
import KPICard from "../../components/KPICard";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  cardBase,
  btnPrimary,
  btnSecondary,
  pageContainer,
} from "../../utils/designTokens";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const CierreRevision = () => {
  const { buildings, selectedBuilding } = useBuilding();
  const buildingFilter =
    selectedBuilding?.buildingid ||
    (buildings && buildings.length === 1 ? buildings[0].buildingid : null);

  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();

  const [loading, setLoading] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [detailCierre, setDetailCierre] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  // Aviso cuando las fichas de los residentes no se pudieron leer: el detalle
  // se muestra igual, con el número de usuario en lugar del nombre.
  const [avisoFichas, setAvisoFichas] = useState(null);
  const [suppliersMap, setSuppliersMap] = useState({});
  const [approving, setApproving] = useState(false);

  // ============================================================
  // Carga de cierres pendientes
  // ============================================================
  const loadPending = useCallback(async () => {
    if (!buildingFilter) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "montlyaccount"),
        where("buildingid", "==", Number(buildingFilter))
      );
      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const pending = all.filter(
        (c) => c.state === "ok" && c.approvalStatus === "pending"
      );
      pending.sort((a, b) =>
        b.year !== a.year ? b.year - a.year : b.month - a.month
      );
      setPendingList(pending);
    } catch (e) {
      console.error(e);
      showToast("Error al cargar cierres pendientes", "error");
    } finally {
      setLoading(false);
    }
  }, [buildingFilter, showToast]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  // ============================================================
  // Abrir detalle de un cierre
  // ============================================================
  const openDetail = async (cierre) => {
    setDetailCierre(cierre);
    setDetailLoading(true);
    setPayments([]);
    setExpenses([]);
    setApartments([]);
    setUsersMap({});
    setAvisoFichas(null);
    setSuppliersMap({});
    try {
      // Pagos y reparto del cierre acotados a SU edificio (reglas 0D).
      const cierreBid = cierre.buildingid ?? buildingFilter;
      const [paySnap, eaSnap, aptSnap] = await Promise.all([
        getDocs(consultaDelCierre(db, "payments", cierre.id, cierreBid)),
        getDocs(consultaDelCierre(db, "expensesapartments", cierre.id, cierreBid)),
        getDocs(
          query(collection(db, "apartments"), where("building", "in", tolerantId(buildingFilter)))
        ),
      ]);

      const payList = paySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const eaList = eaSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPayments(payList);
      setApartments(aptSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Nombres: fichas de los titulares de los pagos del cierre (incluidos
      // los ocupantes salientes), leídas con el edificio fijado (reglas 0D,
      // C10: accessBids array-contains). Si no se pueden leer, el detalle se
      // muestra igual, con el número de usuario, y se avisa: el PDF de la
      // liquidación usa las mismas fichas y al aprobar no se va a adjuntar.
      const titulares = [
        ...new Set(
          payList
            .map((p) => p.userid)
            .filter((u) => u != null && u !== "" && !String(u).startsWith("unit_"))
            .map(String)
        ),
      ];
      if (titulares.length > 0) {
        const sinFichas =
          "No se pudieron leer las fichas de los residentes: la distribución muestra el número de usuario en lugar del nombre y, si aprobás, el aviso sale sin la liquidación en PDF.";
        try {
          const users = await fetchUsersByUserIds(titulares, cierreBid);
          const uMap = {};
          users.forEach((u) => { uMap[String(u.userid)] = u; });
          setUsersMap(uMap);
          if (users.length === 0) setAvisoFichas(sinFichas);
        } catch (usersErr) {
          console.error("Revisión de cierres: no se pudieron leer las fichas de los residentes:", usersErr?.code || usersErr?.message || usersErr);
          setAvisoFichas(sinFichas);
        }
      }

      // Cargar gastos por id con getDoc (una consulta por __name__ no fija el
      // edificio y las reglas 0D la niegan entera).
      const expenseIds = [...new Set(eaList.map((e) => e.expenseId).filter(Boolean))];
      let expenseDocs = [];
      try {
        expenseDocs = await leerGastosPorId(db, expenseIds);
      } catch (expErr) {
        console.error("Error cargando los gastos del cierre:", expErr);
      }
      setExpenses(expenseDocs);

      // Cargar proveedores. Los gastos guardan `supplierid` = el CAMPO numérico
      // supplierId del proveedor (a veces string), NO el id del documento — por
      // eso la query vieja por __name__ no encontraba nada y salía "—". La
      // colección es chica: se carga entera y se indexa por ambas claves.
      try {
        const supSnap = await getDocs(collection(db, "supplier"));
        const sMap = {};
        supSnap.docs.forEach((d) => {
          const data = d.data() || {};
          if (data.supplierId != null && data.supplierId !== "") sMap[String(data.supplierId)] = data;
          sMap[d.id] = data;
        });
        setSuppliersMap(sMap);
      } catch (supErr) {
        console.error("Error cargando proveedores:", supErr);
      }
    } catch (e) {
      console.error(e);
      showToast("Error al cargar el detalle", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  // ============================================================
  // Aprobar cierre
  // ============================================================
  const handleApprove = async () => {
    if (!detailCierre) return;
    const ok = await confirmDialog(
      `¿Aprobar y publicar el cierre de ${MONTHS[detailCierre.month - 1]} ${detailCierre.year}?\n\nLos residentes verán los importes en gastos comunes y cuentas, y podrán registrar el pago.`,
      { title: "Confirmar aprobación", confirmLabel: "Aprobar" }
    );
    if (!ok) return;
    setApproving(true);
    try {
      // 1) PDF de liquidación (el mismo de Cierre de mes) → Storage → link en
      //    el cierre. Va ANTES de aprobar: aprobar publica los pagos, y eso
      //    dispara la Cloud Function que manda el WhatsApp de nueva_expensa con
      //    este PDF adjunto. Best-effort: si falla, se aprueba igual y sale el
      //    aviso sin PDF.
      let pdfOk = false;
      try {
        const { blob, fileName } = await buildLiquidacionPdf({
          buildingId: detailCierre.buildingid ?? buildingFilter,
          cierre: detailCierre,
          selectedBuilding,
          output: "blob",
        });
        const mm = String(detailCierre.month).padStart(2, "0");
        const path = `buildings/${detailCierre.buildingid}/liquidaciones/liquidacion_${detailCierre.year}_${mm}.pdf`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, blob, { contentType: "application/pdf" });
        const url = await getDownloadURL(fileRef);
        await updateDoc(doc(db, "montlyaccount", detailCierre.id), {
          liquidacionPdfUrl: url,
          liquidacionPdfName: fileName,
          liquidacionPdfPath: path,
          liquidacionPdfAt: serverTimestamp(),
        });
        pdfOk = true;
      } catch (pdfErr) {
        console.warn("No se pudo generar/subir el PDF de liquidación (se aprueba igual):", pdfErr);
      }

      await approveMontlyaccountCierre(db, {
        cierreId: detailCierre.id,
        approverEmail: auth.currentUser?.email,
        approverUid: auth.currentUser?.uid,
      });
      showToast(
        pdfOk
          ? "Cierre aprobado y publicado. Los residentes reciben el aviso con la liquidación en PDF."
          : "Cierre aprobado y publicado. (No se pudo adjuntar el PDF: el aviso sale sin la liquidación.)",
        pdfOk ? "success" : "warning"
      );
      setDetailCierre(null);
      loadPending();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Error al aprobar", "error");
    } finally {
      setApproving(false);
    }
  };

  // ============================================================
  // Helpers
  // ============================================================
  const aptForUser = (userId) => {
    const uid = String(userId);
    // Unidades con usuario sintético (sin propietario/inquilino)
    if (uid.startsWith("unit_")) {
      const aptId = uid.replace("unit_", "");
      const apt = apartments.find((a) => String(a.id) === aptId);
      return { apt: apt || null, role: "P" };
    }
    const apt = apartments.find((a) =>
      [a.owner, a.ownerSecondary, a.tenant, a.tenantSecondary]
        .some((x) => String(x) === uid)
    );
    if (!apt) return { apt: null, role: "P" };
    const isOwner = String(apt.owner) === uid || String(apt.ownerSecondary) === uid;
    return { apt, role: isOwner ? "P" : "I" };
  };

  const aptLabelByUser = (userId) => {
    const { apt, role } = aptForUser(userId);
    if (!apt) return "—";
    const unit = apt.number || apt.name || apt.id;
    return `${unit} ${role}`;
  };

  // Resuelve unidad + rol de un PAGO. Prioriza el apartmentId/apartmentNumber
  // guardado en el payment (correcto incluso para ocupantes SALIENTES que ya no
  // figuran en apartments). Cae a la resolución por usuario vigente si el pago no
  // tiene unidad asociada (ej. intereses de deudas sin apartmentId).
  const aptInfoForPayment = (p) => {
    const aptId = p.apartmentId != null && p.apartmentId !== "" ? String(p.apartmentId) : null;
    let apt = aptId ? apartments.find((a) => String(a.id) === aptId) : null;
    if (!apt && !aptId) {
      apt = aptForUser(p.userid).apt;
    }
    const number = p.apartmentNumber || apt?.number || apt?.name || aptId || "";
    // Rol por ocupación vigente; si no matchea (saliente), usar prorationDetail.
    let role = null;
    if (apt) {
      const uid = String(p.userid);
      if (String(apt.owner) === uid || String(apt.ownerSecondary) === uid) role = "P";
      else if (String(apt.tenant) === uid || String(apt.tenantSecondary) === uid) role = "I";
    }
    if (!role) {
      const pd = Array.isArray(p.prorationDetail) && p.prorationDetail[0] ? p.prorationDetail[0] : null;
      if (pd?.role) role = pd.role === "tenant" ? "I" : "P";
    }
    return { apt, number: number ? String(number) : "", role: role || "P" };
  };

  const aptLabelByPayment = (p) => {
    const { number, role } = aptInfoForPayment(p);
    if (!number) return "—";
    return `${number} ${role}`;
  };

  const getSupplierName = (supplierid) => {
    if (supplierid == null || supplierid === "") return "—";
    const sup = suppliersMap[String(supplierid)];
    return sup?.name || sup?.razonSocial || "—";
  };

  const getFundLabel = (expense) => {
    const fund = expense.sourceFund || "caja";
    if (fund === "caja") return "Caja";
    if (fund === "reserva") return "Reserva";
    return friendlyBreakdownName(`fondo_${fund}`, selectedBuilding);
  };

  const totalGastos = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  // Total de los pagos del cierre (la fila TOTAL de la tabla). El KPI "Total
  // distribuido" es lo repartido entre las unidades: sin los alquileres de las
  // unidades del edificio, que se muestran aparte (son ingresos del edificio).
  const totalPagos = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalAlquileres = payments.reduce((s, p) => s + (Number(p.breakdown?.alquiler) || 0), 0);
  const totalDistribuido = totalPagos - totalAlquileres;
  const unidadesQuePagan = apartments.filter((a) => !esUnidadDelEdificio(a)).length;

  // ============================================================
  // Render: sin edificio
  // ============================================================
  if (!buildingFilter) {
    return (
      <div style={styles.container}>
        <ScreenHeader
          icon={FaCalendarCheck}
          title="Revisión de cierres"
          subtitle="Seleccioná un edificio en la barra superior"
        />
        <div style={styles.cardMuted}>
          <p style={{ margin: 0, color: colors.gray600 }}>
            Necesitás un edificio activo para ver los cierres pendientes de aprobación.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // Render: detalle de un cierre
  // ============================================================
  if (detailCierre) {
    // Gastos ordenados por fecha
    const sortedExpenses = [...expenses].sort((a, b) => {
      const da = a.invoiceDate || a.paidDate || "";
      const db2 = b.invoiceDate || b.paidDate || "";
      return da.localeCompare(db2);
    });

    // Pagos ordenados por unidad (menor a mayor), propietario antes que inquilino
    const sortedPayments = [...payments].sort((a, b) => {
      const infoA = aptInfoForPayment(a);
      const infoB = aptInfoForPayment(b);
      const cmp = (infoA.number || "").localeCompare(infoB.number || "", "es", { numeric: true });
      if (cmp !== 0) return cmp;
      // Misma unidad: propietario (P) antes que inquilino (I)
      if (infoA.role !== infoB.role) return infoA.role === "P" ? -1 : 1;
      return 0;
    });

    // Calcular totales del breakdown para la fila TOTAL
    const breakdownTotals = {};
    sortedPayments.forEach((p) => {
      Object.entries(p.breakdown || {}).forEach(([key, val]) => {
        const v = Number(val) || 0;
        if (v > 0) breakdownTotals[key] = (breakdownTotals[key] || 0) + v;
      });
    });

    return (
      <div style={styles.container}>
        <ScreenHeader
          icon={FaCalendarCheck}
          title={`Revisión — ${MONTHS[detailCierre.month - 1]} ${detailCierre.year}`}
          subtitle="Revisión antes de publicar a residentes"
          onBack={() => setDetailCierre(null)}
        />

        {/* Metadata */}
        <div style={styles.metaBox}>
          <div>
            <span style={styles.metaLabel}>Generado por</span>
            <span style={styles.metaValue}>
              {detailCierre.createdByEmail || "—"}
            </span>
          </div>
          <div>
            <span style={styles.metaLabel}>Vencimiento</span>
            <span style={styles.metaValue}>{detailCierre.maturity || "—"}</span>
          </div>
        </div>

        {detailLoading ? (
          <LoadingState variant="block" message="Cargando detalle…" />
        ) : (
          <>
            {/* KPIs */}
            <div style={styles.kpiRow}>
              <KPICard
                icon={FaFileInvoiceDollar}
                iconBg="#e8f4ff"
                iconColor={colors.primary}
                label="Total gastos del período"
                value={formatCurrencyUY(totalGastos)}
              />
              <KPICard
                icon={FaMoneyBillWave}
                iconBg="#d1fae5"
                iconColor={colors.success}
                label="Total distribuido"
                value={formatCurrencyUY(totalDistribuido)}
              />
              {totalAlquileres > 0 && (
                <KPICard
                  icon={FaMoneyBillWave}
                  iconBg="#d1fae5"
                  iconColor={colors.success}
                  label="Alquileres del edificio"
                  value={formatCurrencyUY(totalAlquileres)}
                />
              )}
              <KPICard
                icon={FaUsers}
                iconBg="#fef3c7"
                iconColor={colors.warning}
                label="Unidades"
                value={String(unidadesQuePagan)}
              />
            </div>

            {/* ============ DETALLE DE GASTOS ============ */}
            <h3 style={styles.sectionTitle}>Detalle de Gastos del Mes</h3>
            <div style={styles.tableCard}>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Descripción</th>
                      <th style={styles.th}>Proveedor</th>
                      <th style={styles.th}>Fecha Factura</th>
                      <th style={styles.th}>Fondo</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={styles.emptyCell}>No hay gastos vinculados.</td>
                      </tr>
                    ) : (
                      <>
                        {sortedExpenses.map((e, idx) => (
                          <tr key={e.id} style={idx % 2 === 1 ? { backgroundColor: colors.gray50 } : {}}>
                            <td style={styles.td} title={e.description || ""}>{detalleCorto(e) || "—"}</td>
                            <td style={styles.td}>{getSupplierName(e.supplierid)}</td>
                            <td style={styles.td}>{e.invoiceDate || e.paidDate || "—"}</td>
                            <td style={styles.td}>{getFundLabel(e)}</td>
                            <td style={{ ...styles.td, textAlign: "right" }}>
                              {formatCurrencyUY(Number(e.amount) || 0)}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ backgroundColor: colors.gray100 }}>
                          <td colSpan={4} style={{ ...styles.td, textAlign: "right", color: colors.black }}>
                            TOTAL GASTOS
                          </td>
                          <td style={{ ...styles.td, textAlign: "right", color: colors.black }}>
                            {formatCurrencyUY(totalGastos)}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ============ DISTRIBUCIÓN POR RESIDENTE ============ */}
            <h3 style={styles.sectionTitle}>Distribución por Residente</h3>
            {avisoFichas && (
              <div role="alert" style={styles.avisoFichas}>
                <FaExclamationTriangle size={14} style={{ flexShrink: 0, marginTop: spacing[1] }} aria-hidden="true" />
                <span>{avisoFichas}</span>
              </div>
            )}
            <div style={styles.tableCard}>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Unidad</th>
                      <th style={styles.th}>Residente</th>
                      <th style={styles.th}>Desglose</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPayments.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={styles.emptyCell}>No hay pagos generados.</td>
                      </tr>
                    ) : (
                      <>
                        {sortedPayments.map((p, idx) => {
                          const isSynthetic = String(p.userid).startsWith("unit_");
                          const u = usersMap[String(p.userid)];
                          const name = isSynthetic ? "—" : (u?.name || u?.NombreCompleto || String(p.userid));
                          const unit = aptLabelByPayment(p);
                          const breakdown = p.breakdown || {};
                          const lines = Object.entries(breakdown)
                            .filter(([, v]) => (Number(v) || 0) > 0)
                            .map(([key, val]) => ({
                              label: friendlyBreakdownName(key, selectedBuilding),
                              value: Number(val) || 0,
                            }));

                          return (
                            <tr key={p.id} style={idx % 2 === 1 ? { backgroundColor: colors.gray50 } : {}}>
                              <td style={{ ...styles.td, verticalAlign: "top" }}>{unit}</td>
                              <td style={{ ...styles.td, verticalAlign: "top" }}>{name}</td>
                              <td style={{ ...styles.td, verticalAlign: "top" }}>
                                {lines.map((l, i) => (
                                  <div key={i} style={{ fontSize: typography.sizes.sm, color: colors.gray700, lineHeight: 1.6 }}>
                                    {l.label}: {formatCurrencyUY(l.value)}
                                  </div>
                                ))}
                              </td>
                              <td style={{ ...styles.td, textAlign: "right", verticalAlign: "top" }}>
                                {formatCurrencyUY(Number(p.amount) || 0)}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Fila TOTAL */}
                        <tr style={{ backgroundColor: colors.gray100 }}>
                          <td colSpan={2} style={{ ...styles.td, textAlign: "right", color: colors.black }}>
                            TOTAL
                          </td>
                          <td style={{ ...styles.td, verticalAlign: "top" }}>
                            {Object.entries(breakdownTotals)
                              .filter(([, v]) => v > 0)
                              .map(([key, val], i) => (
                                <div key={i} style={{ fontSize: typography.sizes.sm, color: colors.black, lineHeight: 1.6 }}>
                                  {friendlyBreakdownName(key, selectedBuilding)}: {formatCurrencyUY(val)}
                                </div>
                              ))}
                          </td>
                          <td style={{ ...styles.td, textAlign: "right", color: colors.black }}>
                            {formatCurrencyUY(totalPagos)}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Botón aprobar */}
            <div style={styles.actions}>
              <button
                type="button"
                style={{
                  ...styles.btnApprove,
                  opacity: approving ? 0.7 : 1,
                  cursor: approving ? "not-allowed" : "pointer",
                }}
                onClick={handleApprove}
                disabled={approving || detailLoading}
              >
                {approving ? (
                  <>
                    <FaSpinner className="spin" size={14} /> Publicando…
                  </>
                ) : (
                  <>
                    <FaCheck size={14} /> Aprobar cierre y publicar a residentes
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ============================================================
  // Render: lista de cierres pendientes
  // ============================================================
  return (
    <div style={styles.container}>
      <ScreenHeader
        icon={FaCalendarCheck}
        title="Revisión de cierres"
        subtitle="Cierres calculados pendientes de aprobación"
      />

      {loading ? (
        <LoadingState variant="block" message="Cargando…" />
      ) : pendingList.length === 0 ? (
        <div style={styles.cardMuted}>
          <p style={{ margin: 0, color: colors.gray600 }}>
            No hay cierres pendientes de aprobación para este edificio.
          </p>
        </div>
      ) : (
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Período</th>
                <th style={styles.th}>Alta</th>
                <th style={styles.th}>Generado por</th>
                <th style={styles.th} />
              </tr>
            </thead>
            <tbody>
              {pendingList.map((c, idx) => (
                <tr key={c.id} style={idx % 2 === 1 ? { backgroundColor: colors.gray50 } : {}}>
                  <td style={styles.td}>{MONTHS[c.month - 1]} {c.year}</td>
                  <td style={{ ...styles.td, color: colors.gray600, fontSize: typography.sizes.sm }}>
                    {c.createdAt?.toDate
                      ? c.createdAt.toDate().toLocaleDateString("es-UY")
                      : "—"}
                  </td>
                  <td style={{ ...styles.td, fontSize: typography.sizes.sm }}>{c.createdByEmail || "—"}</td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={styles.btnRevisar}
                      className="habitia-btn-secondary"
                      onClick={() => openDetail(c)}
                    >
                      Revisar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Estilos — design system compliant
// ============================================================
const styles = {
  container: {
    ...pageContainer,
    backgroundColor: colors.gray50,
  },
  cardMuted: {
    ...cardBase,
  },
  metaBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: spacing[5],
    marginBottom: spacing[5],
    padding: spacing[5],
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
  },
  metaLabel: {
    display: "block",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: colors.gray600,
    marginBottom: spacing[1],
    fontWeight: 400,
  },
  metaValue: {
    fontSize: typography.sizes.md,
    color: colors.gray900,
    fontWeight: 400,
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: spacing[4],
    marginBottom: spacing[8],
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: colors.black,
    margin: `0 0 ${spacing[3]}px 0`,
    marginTop: spacing[8],
  },
  tableCard: {
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  avisoFichas: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    marginBottom: spacing[3],
    background: colors.warningLight,
    color: colors.warningDark,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: "12px 16px",
    background: colors.primary,
    color: colors.white,
    fontSize: 11,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 16px",
    fontSize: typography.sizes.md,
    color: colors.gray900,
    fontWeight: 400,
    borderBottom: `1px solid ${colors.gray100}`,
  },
  emptyCell: {
    padding: spacing[5],
    color: colors.gray600,
    fontSize: typography.sizes.md,
    textAlign: "center",
  },
  actions: {
    marginTop: spacing[8],
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing[3],
  },
  btnApprove: {
    ...btnPrimary,
    background: colors.success,
    borderRadius: radius.sm,
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: typography.sizes.md,
  },
  btnRevisar: {
    ...btnSecondary,
    padding: "8px 16px",
    fontSize: typography.sizes.sm,
  },
};

export default CierreRevision;
